import React, { useEffect, useState } from "react";
import { Form, Button, Table, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import * as inventoryService from "../../services/inventoryService";

// Module-scope (not defined inside CategoriesSection's render body) so a new
// function reference isn't created on every parent re-render - nesting it
// would remount every row (losing inline-edit state) each time the list
// refetches, per this codebase's documented nested-component-remount bug.
function CategoryListRow({ item, onRenamed, onToggled }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(item.name);
  }, [item.name]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === item.name) {
      setEditing(false);
      setName(item.name);
      return;
    }
    setSaving(true);
    try {
      const res = await inventoryService.updateCategory(item.id, { name: trimmed, is_active: item.is_active });
      if (res?.success === false) {
        toast.error(res.message || "Could not rename category");
        setName(item.name);
      } else {
        setEditing(false);
        onRenamed?.();
      }
    } catch (err) {
      toast.error(err.message || "Could not rename category");
      setName(item.name);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    try {
      if (item.is_active) {
        await inventoryService.deactivateCategory(item.id);
      } else {
        const res = await inventoryService.updateCategory(item.id, { name: item.name, is_active: 1 });
        if (res?.success === false) {
          toast.error(res.message || "Could not reactivate category");
          return;
        }
      }
      onToggled?.();
    } catch (err) {
      toast.error(err.message || "Could not update category status");
    }
  };

  return (
    <tr>
      <td>
        {editing ? (
          <Form.Control
            size="sm"
            autoFocus
            value={name}
            disabled={saving}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") {
                setEditing(false);
                setName(item.name);
              }
            }}
            onBlur={handleSave}
          />
        ) : (
          <span>{item.name}</span>
        )}
      </td>
      <td>
        <Form.Check
          type="switch"
          checked={!!item.is_active}
          onChange={handleToggle}
          label={item.is_active ? "Active" : "Inactive"}
        />
      </td>
      <td>
        {!editing && (
          <Button size="sm" variant="link" className="p-0" onClick={() => setEditing(true)}>
            Rename
          </Button>
        )}
      </td>
    </tr>
  );
}

function CategoriesSection() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  const loadCategories = () => {
    setLoading(true);
    inventoryService
      .getCategories({ is_active: "all" })
      .then((res) => setCategories(Array.isArray(res?.data) ? res.data : []))
      .catch((err) => toast.error(err.message || "Could not load categories"))
      .finally(() => setLoading(false));
  };

  useEffect(loadCategories, []);

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setAdding(true);
    try {
      const res = await inventoryService.createCategory(trimmed);
      if (res?.success === false) {
        toast.error(res.message || "Could not add category");
      } else {
        setNewName("");
        loadCategories();
      }
    } catch (err) {
      toast.error(err.message || "Could not add category");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div>
      <h5 className="mb-3">Inventory Categories</h5>
      <p className="text-muted" style={{ fontSize: 13 }}>
        Manage the categories used to classify your inventory items. Deactivating a category hides it from item
        forms without affecting items already assigned to it.
      </p>

      <div className="d-flex gap-2 mb-3">
        <Form.Control
          size="sm"
          style={{ maxWidth: 260 }}
          placeholder="New category name…"
          value={newName}
          disabled={adding}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAdd();
          }}
        />
        <Button
          size="sm"
          disabled={!newName.trim() || adding}
          onClick={handleAdd}
          style={{ background: "var(--button-color)", border: "none", color: "#fff" }}
        >
          {adding ? "Adding…" : "+ Add Category"}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-3">
          <Spinner size="sm" animation="border" />
        </div>
      ) : categories.length === 0 ? (
        <div className="text-muted" style={{ fontSize: 13 }}>
          No categories yet — add your first one above.
        </div>
      ) : (
        <Table size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th style={{ width: 140 }}>Status</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => (
              <CategoryListRow key={c.id} item={c} onRenamed={loadCategories} onToggled={loadCategories} />
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default CategoriesSection;
